import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Form, Button, Container, Table, Modal, Row, Col, InputGroup, Pagination } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AddMaker = () => {
  // State variables
  const [cars, setCars] = useState([]);
  const [makers, setMakers] = useState([]);
  const [filteredMakers, setFilteredMakers] = useState([]);
  const [selectedCar, setSelectedCar] = useState('');
  const [makerName, setMakerName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingMakerId, setEditingMakerId] = useState(null);
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [makerToDelete, setMakerToDelete] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  // API base URLs
  const API_BASE_URL = 'https://motor-easy.clickbooster.io/api';
  const CAR_API_URL = `${API_BASE_URL}/cars`;
  const MAKER_API_URL = `${API_BASE_URL}/maker`;

  // Fetch car data for dropdown on component mount
  useEffect(() => {
    fetchCars();
  }, []);

  // Filter makers when search term changes
  useEffect(() => {
    if (makers.length > 0) {
      const filtered = makers.filter(maker =>
        maker.maker_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredMakers(filtered);
      setCurrentPage(1); // Reset to first page on search
    }
  }, [searchTerm, makers]);

  // Fetch cars from API
  const fetchCars = async () => {
    try {
      setLoading(true);
      const response = await axios.get(CAR_API_URL);
      
      if (Array.isArray(response.data)) {
        setCars(response.data);
      } else {
        toast.error('Unexpected response format for cars');
      }
    } catch (error) {
      toast.error(`Error fetching car data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch makers based on selected car
  const fetchMakers = async () => {
    if (selectedCar) {
      try {
        setLoading(true);
        const response = await axios.get(`${MAKER_API_URL}?car_id=${selectedCar}`);
        console.log('Makers response:', response.data);
  
        let makersData = [];
  
        // ✅ Correct extraction from your API response
        if (response.data && Array.isArray(response.data.makers)) {
          makersData = response.data.makers;
        } else {
          toast.error('Could not parse makers data from response');
          setMakers([]);
          setFilteredMakers([]);
          setLoading(false);
          return;
        }
  
        // Sort by ID descending
        const sortedMakers = makersData.sort((a, b) => b.id - a.id);
        setMakers(sortedMakers);
        setFilteredMakers(sortedMakers);
  
        if (sortedMakers.length === 0) {
          toast.info('No makers found for the selected car');
        }
      } catch (error) {
        console.error('Error fetching makers:', error);
        toast.error(`Error fetching makers: ${error.message}`);
        setMakers([]);
        setFilteredMakers([]);
      } finally {
        setLoading(false);
      }
    } else {
      setMakers([]);
      setFilteredMakers([]);
    }
  };
  

  // Effect to fetch makers when car selection changes
  useEffect(() => {
    fetchMakers();
  }, [selectedCar]);

  // Debug function to check actual response structure
  const testEndpoint = async () => {
    if (!selectedCar) {
      toast.warning('Please select a car first');
      return;
    }
    
    try {
      const response = await axios.get(`${MAKER_API_URL}?car_id=${selectedCar}`);
      console.log('Raw response data:', response.data);
      toast.info('Check console for response data');
    } catch (error) {
      console.error('Error testing endpoint:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // Function to handle form submission (Add or Update maker)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCar) {
      toast.warning('Please select a car');
      return;
    }

    if (!makerName.trim()) {
      toast.warning('Maker name cannot be empty');
      return;
    }

    try {
      const payload = {
        car_id: selectedCar,
        maker_name: makerName
      };
      
      console.log('Submitting payload:', payload);
      
      let response;
      
      if (isEditing) {
        // Update existing maker
        response = await axios.put(`${MAKER_API_URL}/${editingMakerId}`, payload);
        toast.success('Maker updated successfully!');
      } else {
        // Create new maker
        response = await axios.post(MAKER_API_URL, payload);
        toast.success('Maker added successfully!');
      }
      
      console.log('Response:', response.data);
      
      // Reset form and refresh maker list
      setMakerName('');
      setIsEditing(false);
      setEditingMakerId(null);
      fetchMakers();
    } catch (error) {
      console.error('Error in submission:', error);
      toast.error(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  // Handle edit button click
  const handleEdit = (maker) => {
    setMakerName(maker.maker_name);
    setEditingMakerId(maker.id);
    setIsEditing(true);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setMakerName('');
    setIsEditing(false);
    setEditingMakerId(null);
  };

  // Handle delete confirmation
  const confirmDelete = (maker) => {
    setMakerToDelete(maker);
    setShowDeleteModal(true);
  };

  // Handle actual deletion
  const handleDelete = async () => {
    try {
      await axios.delete(`${MAKER_API_URL}/${makerToDelete.id}`);
      toast.success('Maker deleted successfully!');
      fetchMakers();
    } catch (error) {
      toast.error(`Error deleting maker: ${error.response?.data?.message || error.message}`);
    } finally {
      setShowDeleteModal(false);
      setMakerToDelete(null);
    }
  };

  // Handle search change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Pagination logic
  const indexOfLastMaker = currentPage * itemsPerPage;
  const indexOfFirstMaker = indexOfLastMaker - itemsPerPage;
  const currentMakers = filteredMakers.slice(indexOfFirstMaker, indexOfLastMaker);
  const totalPages = Math.ceil(filteredMakers.length / itemsPerPage);

  // Generate pagination items
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
      }
    }

    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => setCurrentPage(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  return (
    <Container className="mt-4">
      <ToastContainer />
      <h2 className="mb-4">Maker Management System</h2>

      {/* Car Selection and Maker Form */}
      <Form onSubmit={handleSubmit} className="mb-4">
        <Row>
          <Col md={6}>
            <Form.Group controlId="carSelect" className="mb-3">
              <Form.Label>Select Car</Form.Label>
              <Form.Select
                value={selectedCar}
                onChange={(e) => setSelectedCar(e.target.value)}
              >
                <option value="">-- Select a Car --</option>
                {cars.map((car) => (
                  <option key={car.id} value={car.id}>
                    {car.car_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="makerInput" className="mb-3">
              <Form.Label>{isEditing ? "Edit Maker Name" : "Maker Name"}</Form.Label>
              <Form.Control
                type="text"
                value={makerName}
                onChange={(e) => setMakerName(e.target.value)}
                placeholder="Enter maker name"
              />
            </Form.Group>
          </Col>
        </Row>
        
        <div className="d-flex gap-2">
          <Button variant="primary" type="submit">
            {isEditing ? "Update Maker" : "Add Maker"}
          </Button>
          {isEditing && (
            <Button variant="secondary" onClick={handleCancelEdit}>
              Cancel
            </Button>
          )}
          {process.env.NODE_ENV === 'development' && (
            <Button variant="outline-secondary" onClick={testEndpoint}>
              Test Endpoint
            </Button>
          )}
        </div>
      </Form>

      {/* Search and Maker List Section (only show if a car is selected) */}
      {selectedCar && (
        <>
          {/* Search Filter */}
          <div className="mb-3">
            <InputGroup>
              <Form.Control
                placeholder="Search makers..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <Button 
                  variant="outline-secondary"
                  onClick={() => setSearchTerm("")}
                >
                  Clear
                </Button>
              )}
            </InputGroup>
          </div>

          {/* Debug Info */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mb-3 p-2 bg-light">
              <p className="mb-1"><strong>Debug Info:</strong></p>
              <p className="mb-1">Endpoint: {`${MAKER_API_URL}?car_id=${selectedCar}`}</p>
              <p className="mb-1">Total Makers: {makers.length}</p>
              <p className="mb-0">Filtered Makers: {filteredMakers.length}</p>
            </div>
          )}

          {/* Makers Table */}
          <div className="mt-4">
            <h3>Maker List</h3>
            {loading ? (
              <p>Loading makers...</p>
            ) : filteredMakers.length === 0 ? (
              <p>No makers found. {searchTerm ? "Try a different search term or " : ""}Add a new maker.</p>
            ) : (
              <>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Maker Name</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentMakers.map((maker, index) => (
                      <tr key={maker.id}>
                        <td>{indexOfFirstMaker + index + 1}</td>
                        <td>{maker.maker_name}</td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEdit(maker)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => confirmDelete(maker)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      Showing {indexOfFirstMaker + 1}-{Math.min(indexOfLastMaker, filteredMakers.length)} of {filteredMakers.length} makers
                    </div>
                    <Pagination>
                      <Pagination.First 
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      />
                      <Pagination.Prev 
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      />
                      
                      {renderPaginationItems()}
                      
                      <Pagination.Next 
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      />
                      <Pagination.Last 
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the maker "{makerToDelete?.maker_name}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddMaker;